import { useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { ssoApi, SsoConfig, SsoProvider } from '@/lib/sso';
import { isPlanGateError } from '@/lib/planGate';
import { UpgradeWall } from '@/components/app/UpgradeWall';
import { AppShell } from '@/components/app/AppShell';
import { useCurrentUser } from '@/hooks/useAuth';

export function SsoAdminPage() {
  const { t } = useTranslation();
  const { wsId } = useParams<{ wsId: string }>();
  const qc = useQueryClient();
  const { data: user, isLoading: userLoading } = useCurrentUser();
  if (!userLoading && user?.isAdmin !== true) {
    return <Navigate to={`/app/workspace/${wsId}`} replace />;
  }
  // Scope to the current workspace's company (not companies[0]).
  const { data: workspace } = useQuery({
    queryKey: ['workspace', wsId],
    queryFn: async () => (await api.get<{ companyId: string }>(`/api/workspaces/${wsId}`)).data,
    enabled: !!wsId,
  });
  const companyId = workspace?.companyId;

  const { data: configs = [], isLoading, error: listError } = useQuery({
    queryKey: ['sso-configs', companyId],
    queryFn: () => ssoApi.list(companyId!),
    enabled: !!companyId,
    retry: false,
  });

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<{
    provider: SsoProvider;
    displayName: string;
    entryPoint: string;
    identifier: string;
    secret: string;
    emailDomain: string;
  }>({
    provider: 'saml',
    displayName: '',
    entryPoint: '',
    identifier: '',
    secret: '',
    emailDomain: '',
  });

  const create = useMutation({
    mutationFn: () => ssoApi.create(companyId!, {
      provider: form.provider,
      displayName: form.displayName,
      entryPoint: form.entryPoint,
      identifier: form.identifier,
      secret: form.secret,
      emailDomain: form.emailDomain || undefined,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sso-configs', companyId] });
      setOpen(false);
      setForm({ provider: 'saml', displayName: '', entryPoint: '', identifier: '', secret: '', emailDomain: '' });
    },
  });

  return (
    <AppShell title={t('sso.title')} subtitle={t('sso.subtitle')}>
<main className="mx-auto max-w-5xl px-6 py-10 space-y-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-semibold text-ink-900 tracking-tight">{t('sso.title')}</h1>
            <p className="mt-1 text-ink-600">{t('sso.subtitle')}</p>
          </div>
          <button
            onClick={() => setOpen((v) => !v)}
            className="px-4 py-2 rounded-lg bg-accent-500 text-white text-sm font-semibold hover:bg-accent-600 transition shadow-soft"
          >
            {t('sso.add_config')}
          </button>
        </div>

        <div className="rounded-xl border border-ink-200 bg-white px-4 py-3 text-sm text-ink-700">
          <span className="text-ink-500">{t('sso.metadata_link')}:</span>{' '}
          <a href={ssoApi.metadataUrl()} target="_blank" rel="noreferrer noopener" className="num text-accent-600 hover:underline break-all">
            {ssoApi.metadataUrl()}
          </a>
        </div>

        {open && companyId && (
          <div className="rounded-2xl border border-ink-200 bg-white p-5">
            <h3 className="text-sm font-semibold text-ink-700">{t('sso.add_config')}</h3>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label={t('sso.provider')}>
                <select
                  value={form.provider}
                  onChange={(e) => setForm((f) => ({ ...f, provider: e.target.value as SsoProvider }))}
                  className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm"
                >
                  <option value="saml">{t('sso.provider_saml')}</option>
                  <option value="oidc">{t('sso.provider_oidc')}</option>
                </select>
              </Field>
              <Field label={t('sso.display_name')}>
                <input value={form.displayName} onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))} className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm" />
              </Field>
              <Field label={t('sso.entry_point')} className="md:col-span-2">
                <input value={form.entryPoint} onChange={(e) => setForm((f) => ({ ...f, entryPoint: e.target.value }))} placeholder="https://acme.okta.com/app/.../sso/saml" className="num w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm" />
              </Field>
              <Field label={t('sso.identifier')}>
                <input value={form.identifier} onChange={(e) => setForm((f) => ({ ...f, identifier: e.target.value }))} className="num w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm" />
              </Field>
              <Field label={t('sso.email_domain')}>
                <input value={form.emailDomain} onChange={(e) => setForm((f) => ({ ...f, emailDomain: e.target.value }))} placeholder="acme.com" className="num w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm" />
              </Field>
              <Field label={t('sso.secret')} className="md:col-span-2">
                <textarea value={form.secret} onChange={(e) => setForm((f) => ({ ...f, secret: e.target.value }))} rows={4} placeholder="-----BEGIN CERTIFICATE-----" className="num w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-xs" />
              </Field>
            </div>
            <div className="mt-3 flex gap-2">
              <button
                onClick={() => create.mutate()}
                disabled={create.isPending || !form.displayName || !form.entryPoint || !form.identifier || !form.secret}
                className="px-4 py-2 rounded-lg bg-accent-500 text-white text-sm font-semibold hover:bg-accent-600 disabled:opacity-60"
              >
                {create.isPending ? t('sso.saving') : t('sso.save')}
              </button>
              <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-lg border border-ink-300 text-sm text-ink-700 hover:bg-ink-100">
                {t('common.cancel')}
              </button>
            </div>
            {create.error && (
              <div className="mt-3 rounded-lg bg-warn-500/10 border border-warn-500/30 text-warn-600 px-3 py-2 text-sm">{(create.error as Error).message}</div>
            )}
          </div>
        )}

        {isLoading ? (
          <div className="text-sm text-ink-500">{t('common.loading')}</div>
        ) : listError ? (
          isPlanGateError(listError) ? (
            <UpgradeWall wsId={wsId!} requiredTier="enterprise" featureName={t('sso.feature_name', { defaultValue: 'Single sign-on (SSO)' })} />
          ) : (
            <div className="rounded-2xl border border-warn-500/30 bg-warn-500/10 text-warn-700 px-5 py-4 text-sm">
              {(listError as Error).message}
            </div>
          )
        ) : configs.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-ink-300 bg-white p-12 text-center text-ink-500">{t('sso.empty')}</div>
        ) : (
          <ul className="space-y-3">
            {configs.map((c) => <ConfigRow key={c.id} config={c} companyId={companyId!} t={t} />)}
          </ul>
        )}
      </main>
    </AppShell>
  );
}

type TFn = (k: string, opts?: Record<string, unknown>) => string;

function ConfigRow({ config, companyId, t }: { config: SsoConfig; companyId: string; t: TFn }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [draft, setDraft] = useState({
    displayName: config.displayName,
    entryPoint: config.entryPoint,
    identifier: config.identifier,
    emailDomain: config.emailDomain ?? '',
    secret: '',
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['sso-configs', companyId] });

  const save = useMutation({
    mutationFn: () => ssoApi.update(companyId, config.id, {
      displayName: draft.displayName,
      entryPoint: draft.entryPoint,
      identifier: draft.identifier,
      emailDomain: draft.emailDomain || null,
      secret: draft.secret || undefined,
    }),
    onSuccess: () => { invalidate(); setEditing(false); },
  });
  const toggle = useMutation({
    mutationFn: () => ssoApi.update(companyId, config.id, { enabled: !config.enabled }),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: () => ssoApi.remove(companyId, config.id),
    onSuccess: invalidate,
  });

  if (editing) {
    return (
      <li className="rounded-2xl border border-accent-300 ring-1 ring-accent-500/20 bg-white p-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label={t('sso.display_name')}>
            <input value={draft.displayName} onChange={(e) => setDraft((d) => ({ ...d, displayName: e.target.value }))} className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm" />
          </Field>
          <Field label={t('sso.email_domain')}>
            <input value={draft.emailDomain} onChange={(e) => setDraft((d) => ({ ...d, emailDomain: e.target.value }))} placeholder="acme.com" className="num w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm" />
          </Field>
          <Field label={t('sso.entry_point')} className="md:col-span-2">
            <input value={draft.entryPoint} onChange={(e) => setDraft((d) => ({ ...d, entryPoint: e.target.value }))} className="num w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm" />
          </Field>
          <Field label={t('sso.identifier')}>
            <input value={draft.identifier} onChange={(e) => setDraft((d) => ({ ...d, identifier: e.target.value }))} className="num w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm" />
          </Field>
          <Field label={t('sso.secret_rotate')} className="md:col-span-2">
            <textarea value={draft.secret} onChange={(e) => setDraft((d) => ({ ...d, secret: e.target.value }))} rows={3} placeholder={t('sso.secret_rotate_hint')} className="num w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-xs" />
          </Field>
        </div>
        <div className="mt-3 flex gap-2">
          <button onClick={() => save.mutate()} disabled={save.isPending} className="px-4 py-2 rounded-lg bg-accent-500 text-white text-sm font-semibold hover:bg-accent-600 disabled:opacity-60">
            {save.isPending ? t('sso.saving') : t('common.save')}
          </button>
          <button onClick={() => { setEditing(false); setDraft({ displayName: config.displayName, entryPoint: config.entryPoint, identifier: config.identifier, emailDomain: config.emailDomain ?? '', secret: '' }); }} className="px-4 py-2 rounded-lg border border-ink-300 text-sm text-ink-700 hover:bg-ink-100">
            {t('common.cancel')}
          </button>
          {save.error && <span className="self-center text-xs text-warn-600">{(save.error as Error).message}</span>}
        </div>
      </li>
    );
  }

  return (
    <li className="rounded-2xl border border-ink-200 bg-white p-5">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-base font-semibold text-ink-900">
            {config.displayName}
            {!config.enabled && <span className="ms-2 text-[10px] uppercase tracking-wide text-ink-400 border border-ink-300 rounded px-1.5 py-0.5">{t('sso.disabled')}</span>}
          </h3>
          <p className="text-xs text-ink-500 num">{config.provider} · {config.identifier}</p>
          {config.emailDomain && <p className="text-xs text-ink-500 num">↳ {config.emailDomain}</p>}
        </div>
        <div className="flex items-center gap-3">
          <a href={ssoApi.loginUrl(config.id)} className="text-xs text-accent-600 hover:underline">{t('sso.test_login')} ↗</a>
          <button onClick={() => toggle.mutate()} disabled={toggle.isPending} className="text-xs text-ink-600 hover:text-ink-900">
            {config.enabled ? t('sso.disable') : t('sso.enable')}
          </button>
          <button onClick={() => setEditing(true)} className="text-xs text-ink-600 hover:text-ink-900">{t('common.edit')}</button>
          {!confirmDelete ? (
            <button onClick={() => setConfirmDelete(true)} className="text-xs text-warn-600 hover:text-warn-700">{t('common.delete')}</button>
          ) : (
            <span className="inline-flex items-center gap-2 text-xs">
              <span className="text-ink-600">{t('sso.confirm_delete')}</span>
              <button onClick={() => remove.mutate()} disabled={remove.isPending} className="text-warn-700 font-semibold hover:underline">{t('common.yes')}</button>
              <button onClick={() => setConfirmDelete(false)} className="text-ink-500 hover:underline">{t('common.no')}</button>
            </span>
          )}
        </div>
      </div>
      {remove.error && <p className="mt-2 text-xs text-warn-600">{(remove.error as Error).message}</p>}
    </li>
  );
}

function Field({ label, className, children }: { label: string; className?: string; children: React.ReactNode }) {
  return (
    <label className={`block ${className ?? ''}`}>
      <span className="block text-xs text-ink-500 mb-1">{label}</span>
      {children}
    </label>
  );
}
